import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import DateInputWithArrows from '../components/DateInputWithArrows';
import CostSheetTreeView from '../components/CostSheetTreeView';
import { formatDateEuropean } from '../utils/dateUtils';
import { 
  getProjects, 
  getProjectExpensesForProject,
  createProjectExpense,
  updateProjectExpense,
  deleteProjectExpense,
  getProjectCostSheets,
  getProjectCostSheetCategories,
} from '../services/api';
import { useNotification } from '../context/NotificationContext';
import useResponsive from '../components/responsive/useResponsive';
import ResponsiveDataList from '../components/responsive/ResponsiveDataList';
import { useTranslation } from 'react-i18next';

function ProjectExpenses() {
  const { t, i18n } = useTranslation();
  const { isMobile } = useResponsive();
  const { showNotification } = useNotification();
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [costSheets, setCostSheets] = useState([]);
  const [costSheetCategories, setCostSheetCategories] = useState([]);
  const [projectExpenses, setProjectExpenses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, expenseId: null });
  const [addExpenseDialog, setAddExpenseDialog] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);

  // Form state for add/edit dialog
  const [formData, setFormData] = useState({
    name: '',
    costSheetId: '',
    date: new Date().toISOString().slice(0, 10),
    price: '',
    amount: '',
    notes: ''
  });

  // Load projects on component mount
  useEffect(() => {
    loadProjects();
  }, []);

  // Auto-load project expenses when project is selected
  useEffect(() => {
    if (selectedProject) {
      loadProjectExpenses();
      loadProjectCategories();
    }
  }, [selectedProject]);

  const loadProjects = async () => {
    try {
      const response = await getProjects();
      setProjects(response.data);
    } catch (error) {
      console.error('Error loading projects:', error);
      showNotification(t('notifications.errorLoadingProjects', { defaultValue: 'Error loading projects' }), 'error');
    }
  };

  const loadProjectExpenses = async () => {
    if (!selectedProject) {
      return;
    }

    setLoading(true);
    try {
      const response = await getProjectExpensesForProject(selectedProject);
      setProjectExpenses(response.data);
    } catch (error) {
      console.error('Error loading project expenses:', error);
      showNotification('Error loading project expenses', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadProjectCategories = async () => {
    if (!selectedProject) {
      return;
    }

    try {
      const [categoriesResponse, costSheetsResponse] = await Promise.all([
        getProjectCostSheetCategories(selectedProject),
        getProjectCostSheets(selectedProject)
      ]);
      
      setCostSheetCategories(categoriesResponse.data);
      setCostSheets(costSheetsResponse.data);
    } catch (error) {
      console.error('Error loading project data:', error);
      showNotification('Error loading project data', 'error');
    }
  };

  const handleProjectChange = (event) => {
    setSelectedProject(event.target.value);
    setProjectExpenses([]);
  };

  const handleAddExpense = () => {
    setEditingExpense(null);
    setFormData({
      name: '',
      costSheetId: '',
      date: new Date().toISOString().slice(0, 10),
      price: '',
      amount: '',
      notes: ''
    });
    setAddExpenseDialog(true);
  };

  const handleEditExpense = (expense) => {
    setEditingExpense(expense);
    setFormData({
      name: expense.name,
      costSheetId: expense.costSheetId,
      date: expense.date.slice(0, 10),
      price: expense.price.toString(),
      amount: expense.amount.toString(),
      notes: expense.notes || ''
    });
    setAddExpenseDialog(true);
  };

  const handleDeleteExpense = (expenseId) => {
    setDeleteDialog({ open: true, expenseId });
  };

  const confirmDelete = async () => {
    try {
      await deleteProjectExpense(deleteDialog.expenseId);
      showNotification('Project expense deleted successfully', 'success');
      loadProjectExpenses();
    } catch (error) {
      console.error('Error deleting project expense:', error);
      showNotification('Error deleting project expense', 'error');
    } finally {
      setDeleteDialog({ open: false, expenseId: null });
    }
  };

  const handleCostSheetSelect = (costSheet) => {
    setFormData(prev => ({
      ...prev,
      costSheetId: costSheet.id
    }));
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveExpense = async () => {
    if (!formData.name || !formData.costSheetId || !formData.date || !formData.price || !formData.amount) {
      showNotification('Please fill in all required fields', 'error');
      return;
    }

    setSaving(true);
    try {
      const expenseData = {
        projectId: parseInt(selectedProject),
        costSheetId: parseInt(formData.costSheetId),
        name: formData.name,
        date: formData.date,
        price: parseFloat(formData.price),
        amount: parseFloat(formData.amount),
        notes: formData.notes || null
      };

      if (editingExpense) {
        await updateProjectExpense(editingExpense.id, expenseData);
        showNotification('Project expense updated successfully', 'success');
      } else {
        await createProjectExpense(expenseData);
        showNotification('Project expense created successfully', 'success');
      }

      setAddExpenseDialog(false);
      loadProjectExpenses();
    } catch (error) {
      console.error('Error saving project expense:', error);
      showNotification('Error saving project expense', 'error');
    } finally {
      setSaving(false);
    }
  };

  const getSelectedCostSheet = () => {
    return costSheets.find(cs => cs.id === parseInt(formData.costSheetId));
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat(i18n.language, {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>{t('nav.projectExpenses')}</Typography>
      
      {/* Project Selection */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>{t('projectTimelines.selectProject')}</InputLabel>
                <Select
                  value={selectedProject}
                  onChange={handleProjectChange}
                  label={t('projectTimelines.selectProject')}
                >
                  {projects.map((project) => (
                    <MenuItem key={project.id} value={project.id}>
                      {project.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddExpense}
                disabled={!selectedProject}
                fullWidth
              >
                {t('common.add')}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Project Expenses Table */}
      {selectedProject && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Project Expenses for {projects.find(p => p.id === parseInt(selectedProject))?.name}
            </Typography>
            
            {loading ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            ) : projectExpenses.length === 0 ? (
              <Alert severity="info">
                No project expenses found for this project.
              </Alert>
            ) : isMobile ? (
              <ResponsiveDataList
                rows={projectExpenses}
                getTitle={(expense) => expense.name}
                getSubtitle={(expense) => `${formatDateEuropean(expense.date)} - ${formatCurrency(expense.price * expense.amount)}`}
                renderFields={(expense) => (
                  <Box>
                    <Typography variant="body2"><b>Cost sheet:</b> {expense.costSheet.code ? `${expense.costSheet.code} - ` : ''}{expense.costSheet.name}</Typography>
                    <Typography variant="body2"><b>Amount:</b> {expense.amount}</Typography>
                    <Typography variant="body2"><b>Unit price:</b> {formatCurrency(expense.price)}</Typography>
                  </Box>
                )}
                renderActions={(expense) => (
                  <>
                    <Button size="small" onClick={() => handleEditExpense(expense)}>Edit</Button>
                    <Button size="small" color="error" onClick={() => handleDeleteExpense(expense.id)}>Delete</Button>
                  </>
                )}
              />
            ) : (
              <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Cost Sheet</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Price</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Total</TableCell>
                      <TableCell>Notes</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {projectExpenses.map((expense) => (
                      <TableRow key={expense.id}>
                        <TableCell>{expense.name}</TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {expense.costSheet.code ? `${expense.costSheet.code} – ` : ''}{expense.costSheet.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {expense.costSheet.category.name}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{formatDateEuropean(expense.date)}</TableCell>
                        <TableCell>{formatCurrency(expense.price)}</TableCell>
                        <TableCell>{expense.amount}</TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {formatCurrency(expense.price * expense.amount)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {expense.notes && (
                            <Tooltip title={expense.notes}>
                              <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                                {expense.notes}
                              </Typography>
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => handleEditExpense(expense)}
                            color="primary"
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteExpense(expense.id)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {/* Add/Edit Expense Dialog */}
      <Dialog 
        open={addExpenseDialog} 
        onClose={() => setAddExpenseDialog(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          {editingExpense ? 'Edit Project Expense' : 'Add Project Expense'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Expense Name"
                value={formData.name}
                onChange={(e) => handleFormChange('name', e.target.value)}
                required
              />
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Select Cost Sheet
              </Typography>
              {formData.costSheetId && (
                <Chip
                  label={getSelectedCostSheet()?.name || 'Selected Cost Sheet'}
                  onDelete={() => handleFormChange('costSheetId', '')}
                  color="primary"
                  sx={{ mb: 2 }}
                />
              )}
              <Box sx={{ maxHeight: 300, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
                <CostSheetTreeView
                  categories={costSheetCategories}
                  onCostSheetSelect={handleCostSheetSelect}
                  selectedCostSheetId={formData.costSheetId}
                />
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <DateInputWithArrows
                label={t('personnel.fields.date')}
                value={formData.date}
                onChange={(e) => handleFormChange('date', e.target.value)}
                required
                fullWidth
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Amount"
                type="number"
                value={formData.amount}
                onChange={(e) => handleFormChange('amount', e.target.value)}
                required
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Price per Unit"
                type="number"
                value={formData.price}
                onChange={(e) => handleFormChange('price', e.target.value)}
                required
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Total Cost"
                value={formData.price && formData.amount ? 
                  formatCurrency(parseFloat(formData.price) * parseFloat(formData.amount)) : 
                  formatCurrency(0)
                }
                disabled
                InputProps={{
                  readOnly: true,
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('personnel.fields.notes')}
                multiline
                rows={3}
                value={formData.notes}
                onChange={(e) => handleFormChange('notes', e.target.value)}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
            <Button onClick={() => setAddExpenseDialog(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSaveExpense}
            variant="contained"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
          >
            {editingExpense ? t('common.save') : t('common.create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, expenseId: null })}
        fullScreen={isMobile}
      >
        <DialogTitle>{t('common.delete')}</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this project expense? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, expenseId: null })}>
            {t('common.cancel')}
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ProjectExpenses;

